import { Injectable, signal } from '@angular/core';
import { Observable, isObservable, firstValueFrom } from 'rxjs';
import { friendlyError } from '../utils/friendly-error';

export interface DialogField {
  name: string;
  label: string;
  type?: 'text' | 'password' | 'number' | 'textarea' | 'checkbox';
  value?: string | number | boolean;
  placeholder?: string;
  required?: boolean;
  min?: number;
  step?: number;
  hint?: string;
}

export interface DialogConfig {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  tone?: 'info' | 'warning' | 'danger' | 'success';
  fields?: DialogField[];
}

export interface ResultFlash {
  tone: 'success' | 'error' | 'info';
  message: string;
  // Enlace opcional mostrado como botón en la etapa de resultado (p. ej.
  // "Ver resultado de pago" cuando reenviar un enlace ya no aplica porque
  // la reserva se pagó mientras tanto) — abre en pestaña nueva.
  actionLabel?: string;
  actionUrl?: string;
}

export type DialogStage = 'confirm' | 'busy' | 'result';

/**
 * Estado unificado de UN SOLO modal compartido. Antes confirmAndRun() pasaba
 * por tres señales independientes (current / busyMessage / resultFlash),
 * cada una detrás de su propio *ngIf en FeedbackCenterComponent — al pasar
 * de una etapa a la siguiente, el *ngIf de la anterior se volvía false y el
 * de la siguiente se volvía true, así que técnicamente eran tres overlays
 * distintos que se montaban/desmontaban en secuencia. Visualmente se sentía
 * como "dos o tres ventanas" en vez de una sola transición fluida. Ahora es
 * UN objeto que va cambiando de `stage` sin que el modal se cierre entre
 * medio — FeedbackCenterComponent renderiza un único overlay/card y
 * conmuta el contenido interno según `stage`.
 */
export interface DialogState {
  stage: DialogStage;
  config: DialogConfig;
  busyText?: string;
  result?: ResultFlash;
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  readonly state = signal<DialogState | null>(null);
  private resolver: ((value: Record<string, string | boolean> | null) => void) | null = null;
  private resultTimer: ReturnType<typeof setTimeout> | null = null;
  // Cuando confirmAndRun() está orquestando el flujo completo, finish() no
  // debe cerrar el modal del todo al confirmar: debe encadenar directo a la
  // etapa "busy" dentro del MISMO modal. Para un open()/confirm() sueltos
  // (sin confirmAndRun), finish() sigue cerrando de inmediato como antes.
  private chainToBusy = false;

  open(config: DialogConfig): Promise<Record<string, string | boolean> | null> {
    if (this.resolver) this.resolver(null);
    if (this.resultTimer) { clearTimeout(this.resultTimer); this.resultTimer = null; }
    this.chainToBusy = false;
    this.state.set({ stage: 'confirm', config });
    return new Promise(resolve => { this.resolver = resolve; });
  }

  async confirm(config: Omit<DialogConfig, 'fields'>): Promise<boolean> {
    return (await this.open(config)) !== null;
  }

  /**
   * Confirma, ejecuta la acción real y muestra el resultado (procesando ->
   * éxito/error) sin cerrar y reabrir el modal: las tres etapas viven en el
   * mismo `state`. El éxito se cierra solo a los 1.6s (o antes con un
   * click); el error se deja abierto hasta que el usuario lo cierre, para
   * que no se pierda el mensaje de fallo.
   */
  async confirmAndRun<T>(
    config: DialogConfig,
    action: (values: Record<string, string | boolean>) => Observable<T> | Promise<T>,
    // Mensaje fijo (uso normal), o una función que arma el flash a partir
    // del resultado real de la acción — para cuando la respuesta puede
    // traer un desenlace distinto de "éxito liso" (p. ej. "ya estaba
    // pagada, aquí tienes el enlace para verlo" en vez de "enviado").
    successMessage?: string | ((result: T) => ResultFlash | string),
    mapError?: (error: unknown) => string,
  ): Promise<T | null> {
    const opened = this.open(config);
    this.chainToBusy = true;
    const values = await opened;
    if (values === null) return null;

    try {
      const raw = action(values);
      const result = await (isObservable(raw) ? firstValueFrom(raw) : raw);
      const flash = typeof successMessage === 'function' ? successMessage(result) : successMessage;
      this.showResult(typeof flash === 'string' || !flash ? { tone: 'success', message: flash || 'Listo.' } : flash);
      return result;
    } catch (error) {
      const message = mapError ? mapError(error) : friendlyError(error, 'Ocurrió un error al procesar la acción.');
      this.showResult({ tone: 'error', message });
      throw error;
    }
  }

  /**
   * Ejecuta una acción de una sola vez (mismas etapas "procesando ->
   * éxito/error" que confirmAndRun, mismo modal), pero SIN la etapa previa
   * de confirmación: para acciones de pago, que no deben llevar un paso de
   * "¿estás seguro?" adicional al propio formulario que ya las dispara.
   */
  async run<T>(
    busyText: string,
    action: () => Observable<T> | Promise<T>,
    successMessage?: string | ((result: T) => ResultFlash | string),
    mapError?: (error: unknown) => string,
    tone: DialogConfig['tone'] = 'info',
  ): Promise<T | null> {
    if (this.resolver) { this.resolver(null); this.resolver = null; }
    if (this.resultTimer) { clearTimeout(this.resultTimer); this.resultTimer = null; }
    this.chainToBusy = false;
    this.state.set({ stage: 'busy', config: { title: '', tone }, busyText });

    try {
      const raw = action();
      const result = await (isObservable(raw) ? firstValueFrom(raw) : raw);
      const flash = typeof successMessage === 'function' ? successMessage(result) : successMessage;
      this.showResult(typeof flash === 'string' || !flash ? { tone: 'success', message: flash || 'Listo.' } : flash);
      return result;
    } catch (error) {
      const message = mapError ? mapError(error) : friendlyError(error, 'Ocurrió un error al procesar la acción.');
      this.showResult({ tone: 'error', message });
      throw error;
    }
  }

  private showResult(flash: ResultFlash): void {
    if (this.resultTimer) clearTimeout(this.resultTimer);
    this.state.update(s => s ? { ...s, stage: 'result', result: flash } : s);
    if (flash.tone === 'success') {
      this.resultTimer = setTimeout(() => this.dismissResult(), 2600);
    }
  }

  dismissResult(): void {
    if (this.resultTimer) { clearTimeout(this.resultTimer); this.resultTimer = null; }
    this.state.set(null);
  }

  finish(values: Record<string, string | boolean>): void {
    const resolve = this.resolver;
    this.resolver = null;
    if (this.chainToBusy) {
      this.chainToBusy = false;
      this.state.update(s => s ? { ...s, stage: 'busy', busyText: s.config.confirmText ? `${s.config.confirmText}…` : 'Procesando…' } : s);
    } else {
      this.state.set(null);
    }
    resolve?.(values);
  }

  cancel(): void {
    this.chainToBusy = false;
    const resolve = this.resolver;
    this.resolver = null;
    this.state.set(null);
    resolve?.(null);
  }
}
